import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TabsComponent from '@shared/components/tabs/tabs';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import GenreAddDialog from '@modules/entertainment/components/genre-add-dialog/genre-add-dialog';
import { GenresEnum } from '@modules/entertainment/models/entertainment.models';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog';
import { LanguageEnum } from '@shared/models/language.model';
import { MoviesDialogFormProps } from '@modules/entertainment/models/movies.model';
import dayjs from 'dayjs';
import { CrudActionEnum } from '@models/app-autocomplete.model.ts';
import useLanguageStore from '@store/language-store/language.store';
import { validateNotOnlySpaces } from '@shared/utils/trim-form-values-utils';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import AppAutocompleteWithActions from '@components/app-autocomplete-with-actions/app-autocomplete-with-actions.tsx';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import { FileTypeEnum } from '@models/media-library.model.ts';
import { moviesErrorMessages } from '@modules/entertainment/utils/movies.utils.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import useMoviesStore from '@modules/entertainment/pages/movies/store/movies.store.ts';

const MovieDialogContent: React.FC<MoviesDialogFormProps> = ({
  selectedItem,
  genreList,
  onSave
}) => {
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    trigger
  } = useForm<any>({ mode: 'onTouched' });

  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();

  const { isLoading, createGenre, editGenre, deleteGenre, isGenreLoading } =
    useMoviesStore();
  const { languageList } = useLanguageStore((state) => state);
  const { resetSelectedMedia } = useMediaLibraryStore();

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showAddNewGenreDialog, setShowAddNewGenreDialog] = useState(false);
  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = useState<string>(defaultLang);
  const [genresLists, setGenresLists] = useState<any[]>([]);
  const [selectedGenreValue, setSelectedGenreValue] = useState<any>(null);

  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const [multiLangFields, setMultiLangFields] = useState<{
    title: Record<string, string | undefined>;
    description: Record<string, string | undefined>;
  }>({
    title: {},
    description: {}
  });

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentTitle = watch('title');
    const currentDescription = watch('description');

    setMultiLangFields((prev) => ({
      title: { ...prev.title, [currentTab]: currentTitle },
      description: { ...prev.description, [currentTab]: currentDescription }
    }));

    setCurrentTab(newLang);
    setValue('title', multiLangFields.title?.[newLang] || '');
    setValue('description', multiLangFields.description?.[newLang] || '');
  };

  const onSubmit = (data: any) => {
    const currentTitle = watch('title');
    const currentDescription = watch('description');

    const fullData: any = {
      title: { ...multiLangFields.title, [currentTab]: currentTitle },
      description: {
        ...multiLangFields.description,
        [currentTab]: currentDescription
      },
      url: data.url,
      date: data.date,
      backdrop: selectedMedia.backdropImage?.path || null,
      poster: selectedMedia.posterImage?.path || null,
      genres: genresLists.map(({ id }) => id)
    };

    onSave(fullData);
  };

  const handleSaveGenre = async (value: any) => {
    if (selectedGenreValue?.id) {
      editGenre({
        data: value,
        id: selectedGenreValue?.id,
        handleClose: handleCloseGenreDialog
      });
    } else {
      createGenre({
        data: value,
        handleClose: handleCloseGenreDialog
      });
    }
  };

  const handleDeleteConfirm = async () => {
    deleteGenre({
      id: selectedGenreValue?.id,
      handleClose: () => setShowDeleteDialog(false)
    });
  };

  const handleCloseGenreDialog = () => {
    resetSelectedMedia('genreImage');
    setShowAddNewGenreDialog(false);
    setShowDeleteDialog(false);
    setSelectedGenreValue({ id: '', name: '' });
  };

  const itemActionHandler = (value: any, type: CrudActionEnum) => {
    if (type === CrudActionEnum.Add) {
      setSelectedGenreValue({ name: { [currentTab]: value } });
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Edit) {
      setSelectedGenreValue(value);
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Delete) {
      setSelectedGenreValue(value);
      setShowDeleteDialog(true);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const { title, description } = selectedItem;

      const date = selectedItem.date ? dayjs(selectedItem.date) : null;

      setMultiLangFields({ title, description });

      const genreObjects =
        selectedItem.genres?.map((genre: any) => ({
          ...genre,
          id: genre._id,
          name: genre.name || Object.values(genre.name)[0]
        })) || [];

      reset({
        title: title[defaultLang] || '',
        description: description[defaultLang] || '',
        url: selectedItem.url || '',
        date: date,
        genres: genreObjects
      });

      setGenresLists(genreObjects);
    }
  }, [defaultLang, selectedItem, reset]);

  return (
    <div>
      <div className="w-full mb-4">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>
      <div>
        <form className="flex flex-col gap-1">
          <div className="flex gap-4 mb-[15px]">
            <div className="media-wrapper">
              <div className="flex mb-1.5">
                {t('label_movie_backdrop')}
                <InfoTooltip text="tooltip_info_movies_backdrop" />
              </div>
              <MediaLibraryComponent
                width={240}
                height={120}
                mediaKey={'backdropImage'}
                allowedTypes={[FileTypeEnum.Image]}
                path={selectedItem?.backdrop}
                onSelect={(media) =>
                  setSelectedMedia((prev: any) => ({
                    ...prev,
                    backdropImage: media
                  }))
                }
              />
            </div>

            <div className="media-wrapper">
              <div className="flex mb-1.5">
                {t('label_movie_poster')}
                <InfoTooltip text="tooltip_info_movies_poster" />
              </div>
              <MediaLibraryComponent
                width={240}
                height={120}
                mediaKey={'posterImage'}
                allowedTypes={[FileTypeEnum.Image]}
                path={selectedItem?.poster}
                onSelect={(media) =>
                  setSelectedMedia((prev: any) => ({
                    ...prev,
                    posterImage: media
                  }))
                }
              />
            </div>
          </div>
          <div className="w-full flex gap-7">
            <div className="w-full flex flex-col gap-1">
              <FormControl error={!!errors.title}>
                <FormLabel className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_movie_title')}
                  <InfoTooltip text="tooltip_info_movies_title" />
                </FormLabel>
                <OutlinedInput
                  {...register('title', {
                    required: true,
                    validate: {
                      notOnlySpaces: validateNotOnlySpaces
                    }
                  })}
                  error={!!errors.title}
                  placeholder={t('placeholder_movie_title')}
                />
                <FormHelperText>
                  {errors?.title &&
                    getErrorMessage({
                      errors,
                      fieldName: 'title',
                      errorMessages: moviesErrorMessages
                    })}
                </FormHelperText>
              </FormControl>

              <div className="w-full flex flex-col gap-4">
                <FormControl className="w-full" error={!!errors.date}>
                  <FormLabel className="flex">
                    <span className="reuqired-symbole">*</span>
                    {t('label_movie_date_range')}
                    <InfoTooltip text="tooltip_info_movies_date_range" />
                  </FormLabel>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          value={field.value || null}
                          onChange={(newValue) => field.onChange(newValue)}
                          onClose={() => {
                            trigger('date');
                          }}
                          slotProps={{
                            textField: {
                              error: !!errors.date,
                              placeholder: t(
                                'new_reservation_details_form_date_of_birth_placeholder'
                              ),
                              onBlur: async (event: any) => {
                                if (
                                  event.relatedTarget?.closest(
                                    '[role="dialog"]'
                                  )
                                )
                                  return;

                                field.onBlur();
                                await trigger('date');
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <FormHelperText>
                    {errors?.date &&
                      getErrorMessage({
                        errors,
                        fieldName: 'date',
                        errorMessages: moviesErrorMessages
                      })}
                  </FormHelperText>
                </FormControl>
              </div>

              <FormControl>
                <FormLabel className="flex">
                  {t('label_movie_description')}
                  <InfoTooltip text="tooltip_info_movies_description" />
                </FormLabel>
                <OutlinedInput
                  multiline
                  rows={4}
                  {...register('description', {
                    required: false
                  })}
                  placeholder={t('placeholder_movie_description')}
                />
              </FormControl>
            </div>

            <div className="w-full flex flex-col gap-2">
              <FormControl error={!!errors.url} className="w-full">
                <FormLabel className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_movie_url')}
                  <InfoTooltip text="tooltip_info_movies_url" />
                </FormLabel>
                <OutlinedInput
                  rows={4}
                  {...register('url', {
                    required: true,
                    pattern:
                      /^(https?:\/\/)([a-z\d.-]+)(\.[a-z\d.-]+)+(:\d+)?(\/[^\s]*)?$/i,
                    validate: {
                      notOnlySpaces: validateNotOnlySpaces
                    }
                  })}
                  error={!!errors.url}
                  placeholder={t('placeholder_movie_url')}
                />
                <FormHelperText>
                  {errors?.url &&
                    getErrorMessage({
                      errors,
                      fieldName: 'url',
                      errorMessages: moviesErrorMessages
                    })}
                </FormHelperText>
              </FormControl>

              <AppAutocompleteWithActions
                name="genres"
                control={control}
                label={
                  <span className="flex">
                    <span className="reuqired-symbole">*</span>
                    {t('label_genres')}
                    <InfoTooltip text="tooltip_info_movies_genres" />
                  </span>
                }
                placeholder={t('placeholder_movie_genres')}
                language={currentTab}
                options={genreList}
                selectedList={genresLists}
                onChange={setGenresLists}
                itemActionHandler={itemActionHandler}
                validationProps={{
                  errors,
                  errorMessages: moviesErrorMessages,
                  getErrorMessage,
                  validationRules: { required: true }
                }}
              />
            </div>
          </div>

          <div className="flex justify-end items-center gap-6 pt-4 pb-1 sticky bottom-0 bg-white">
            <Button
              disabled={!isValid}
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              loading={isLoading()}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </div>
      <AppDialog
        open={showAddNewGenreDialog}
        title={t('entertainment_add_genre')}
        content={
          <GenreAddDialog
            loading={isGenreLoading()}
            selectedValue={selectedGenreValue}
            type={GenresEnum.Movies}
            handleSave={handleSaveGenre}
          />
        }
        onClose={handleCloseGenreDialog}
      />
      <div className="w-full h-full">
        <DeleteDialog
          onCancel={handleCloseGenreDialog}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          title={t('delete_popup_title')}
          isLoading={isGenreLoading()}
        />
      </div>
    </div>
  );
};

export default MovieDialogContent;
